import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Video, Loader2, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/ui/sonner";

interface Props {
  classes: { id: string; name: string }[];
  lessons: { id: string; title: string; class_id: string | null }[];
  children: { id: string; full_name: string; class_id: string | null }[];
  nurseryId: string;
  onSuccess: () => void;
}

const AddLiveSessionDialog = ({ classes, lessons, children, nurseryId, onSuccess }: Props) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [targetType, setTargetType] = useState<"class" | "students">("class");
  const [selectedChildren, setSelectedChildren] = useState<string[]>([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    meeting_url: "",
    meeting_platform: "zoom",
    class_id: "",
    lesson_id: "",
    scheduled_at: "",
    duration_minutes: "30",
  });

  const filteredLessons = lessons.filter((l) => !form.class_id || l.class_id === form.class_id);
  const filteredChildren = children.filter((c) => !form.class_id || c.class_id === form.class_id);

  const toggleChild = (id: string) => {
    setSelectedChildren((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (!form.title || !form.meeting_url || !form.scheduled_at) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }
    if (targetType === "class" && !form.class_id) {
      toast.error("يرجى اختيار الفصل");
      return;
    }
    if (targetType === "students" && selectedChildren.length === 0) {
      toast.error("يرجى اختيار طالب واحد على الأقل");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.from("live_sessions").insert({
        nursery_id: nurseryId,
        title: form.title,
        description: form.description || null,
        meeting_url: form.meeting_url,
        meeting_platform: form.meeting_platform,
        class_id: targetType === "class" ? form.class_id : null,
        lesson_id: form.lesson_id || null,
        scheduled_at: form.scheduled_at,
        duration_minutes: parseInt(form.duration_minutes) || 30,
        created_by: user!.id,
      } as any);
      if (error) throw error;
      toast.success("تم إنشاء الجلسة وإرسال إشعارات لأولياء الأمور");
      setOpen(false);
      resetForm();
      onSuccess();
    } catch (e: any) {
      toast.error(e.message || "حدث خطأ");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ title: "", description: "", meeting_url: "", meeting_platform: "zoom", class_id: "", lesson_id: "", scheduled_at: "", duration_minutes: "30" });
    setSelectedChildren([]);
    setTargetType("class");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          إنشاء بث مباشر
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-primary" />
            إنشاء جلسة بث مباشر
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh]">
          <div className="space-y-4 mt-2 px-1">
            <div className="space-y-2">
              <Label>عنوان الجلسة *</Label>
              <Input
                placeholder="مثال: مراجعة درس الرياضيات"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>

            {/* Target type selection */}
            <div className="space-y-2">
              <Label>الجلسة موجهة لـ *</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={targetType === "class" ? "default" : "outline"}
                  onClick={() => { setTargetType("class"); setSelectedChildren([]); }}
                >
                  فصل كامل
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={targetType === "students" ? "default" : "outline"}
                  onClick={() => { setTargetType("students"); setForm({ ...form, class_id: "" }); }}
                >
                  طلاب محددين
                </Button>
              </div>
            </div>

            {targetType === "class" && (
              <div className="space-y-2">
                <Label>الفصل *</Label>
                <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v, lesson_id: "" })}>
                  <SelectTrigger><SelectValue placeholder="اختر الفصل" /></SelectTrigger>
                  <SelectContent>
                    {classes.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {targetType === "students" && (
              <div className="space-y-2">
                <Label>اختيار فصل للتصفية (اختياري)</Label>
                <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
                  <SelectTrigger><SelectValue placeholder="جميع الفصول" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع الفصول</SelectItem>
                    {classes.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Label>اختر الطلاب *</Label>
                <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                  {(form.class_id && form.class_id !== "all" ? filteredChildren : children).length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center">لا يوجد طلاب</p>
                  ) : (
                    (form.class_id && form.class_id !== "all" ? filteredChildren : children).map((child) => (
                      <div key={child.id} className="flex items-center gap-2">
                        <Checkbox
                          id={child.id}
                          checked={selectedChildren.includes(child.id)}
                          onCheckedChange={() => toggleChild(child.id)}
                        />
                        <label htmlFor={child.id} className="text-sm cursor-pointer">{child.full_name}</label>
                      </div>
                    ))
                  )}
                </div>
                {selectedChildren.length > 0 && (
                  <p className="text-xs text-muted-foreground">تم اختيار {selectedChildren.length} طالب</p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>المنصة</Label>
                <Select value={form.meeting_platform} onValueChange={(v) => setForm({ ...form, meeting_platform: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zoom">Zoom</SelectItem>
                    <SelectItem value="google_meet">Google Meet</SelectItem>
                    <SelectItem value="teams">Microsoft Teams</SelectItem>
                    <SelectItem value="other">أخرى</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>المدة (دقيقة)</Label>
                <Input
                  type="number"
                  min="5"
                  max="180"
                  value={form.duration_minutes}
                  onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>رابط الاجتماع *</Label>
              <Input
                placeholder="https://zoom.us/j/..."
                value={form.meeting_url}
                onChange={(e) => setForm({ ...form, meeting_url: e.target.value })}
                dir="ltr"
              />
            </div>

            <div className="space-y-2">
              <Label>الموعد *</Label>
              <Input
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
              />
            </div>

            {filteredLessons.length > 0 && targetType === "class" && (
              <div className="space-y-2">
                <Label>ربط بدرس (اختياري)</Label>
                <Select value={form.lesson_id} onValueChange={(v) => setForm({ ...form, lesson_id: v })}>
                  <SelectTrigger><SelectValue placeholder="اختر درس..." /></SelectTrigger>
                  <SelectContent>
                    {filteredLessons.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>وصف (اختياري)</Label>
              <Textarea
                placeholder="تفاصيل إضافية عن الجلسة..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
              />
            </div>

            <Button onClick={handleSubmit} disabled={loading} className="w-full gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              إنشاء الجلسة وإرسال إشعارات
            </Button>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default AddLiveSessionDialog;
